import type { NavigationIntent } from "./chat.types"
import { getCurrentPageContext, parsePageUrl } from "@/lib/navigation"

/**
 * Build a `NavigationIntent` from a raw `navigate_ui` tool payload. The backend
 * may hand the arguments through `approval.required`, `tool.started` or
 * `tool.completed` events — normalise them here so surfaces treat them the same.
 */
export function toNavigationIntent(
  toolName: string,
  raw?: unknown,
  actionId?: string,
  fallback?: Partial<NavigationIntent>
): NavigationIntent {
  const args = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  return {
    actionId,
    toolName,
    page: (args.page as string | undefined) ?? fallback?.page,
    params: (args.params as Record<string, string> | undefined) ?? fallback?.params,
    filters: (args.filters as Record<string, string> | undefined) ?? fallback?.filters,
    rawArguments: raw,
  }
}

export function isNavigateTool(toolName: string): boolean {
  return toolName === "navigate_ui"
}

export function isPageContextTool(toolName: string): boolean {
  return toolName === "get_page_context"
}

/**
 * Builds the JSON result returned for a `get_page_context` tool call. Parses the
 * supplied URL when present, otherwise the user's current browser URL. Used by
 * both chat surfaces so the model sees the same shape.
 */
export function buildPageContextResult(args?: unknown): string {
  const raw = args && typeof args === "object" ? (args as Record<string, unknown>) : {}
  const url = typeof raw.url === "string" && raw.url.trim() ? raw.url : undefined
  const context = url ? parsePageUrl(url) : getCurrentPageContext()
  return JSON.stringify(context ?? { error: "Could not parse the current page context." })
}

const TITLE_WHITESPACE = /\s+/g

export function deriveConversationTitle(content: string, maxLength = 48): string {
  const cleaned = content.replace(TITLE_WHITESPACE, " ").trim()
  if (!cleaned) return ""
  const ellipsis = cleaned.length > maxLength ? "…" : ""
  return `${cleaned.slice(0, maxLength)}${ellipsis}`
}